import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import sidebarTrackingMixin from "@/assets/mixins/sidebarTrackingMixin";
import { checkPermissions, featureAccess } from '@/utils/accesses'
import appConfig from '@/utils/appConfig'

Vue.use(VueRouter)

const defaultRouteName = appConfig.VUE_APP_DEFAULT_START_PAGE || 'dashboard'

/**
 * `meta.menu` puts a route into the navigation (sidebar + mission-control grid).
 * Declare it once here and the item shows up everywhere with its access rules:
 * `section`/`order` place it, `icon`/`activeIcon` draw it, `feature`,
 * `permissions`, `notRoles` and `adminFallback` (PM_FALLBACK ? permissions :
 * fallbackRoles) gate it. An optional `create` puts a quick-create button on
 * the grid tile: `label` is its tooltip, `route` sends it to a dedicated page,
 * otherwise the tile's own route opens with ?state=create (handled by
 * createOnOpenMixin on the page); `permissions`/`roles` gate the button.
 * Resolved by src/assets/mixins/menuLinksMixin.js.
 */
const routes = [
  {
    path: '/',
    name: 'main',
    redirect: { name: defaultRouteName },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../pages/login'),
    meta: {
      title: 'Login'
    },
  },
  {
    path: '/enter-code',
    name: 'enter-code',
    component: () => import(/* webpackChunkName: "enter-code" */ '../pages/login/enter-code.vue'),
    meta: {
      title: 'Verify by email'
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import(/* webpackChunkName: "register" */ '../pages/register'),
    meta: {
      title: 'Register'
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => {
      return featureAccess('PM_FALLBACK')
        ? import(/* webpackChunkName: "users" */ '../pages/users')
        : import(/* webpackChunkName: "users-old" */ '../pages/users-old')
    },
    meta: {
      menu: {
        section: 'admin',
        order: 10,
        title: 'Organizations',
        icon: 'I-S-Organizations',
        ...(featureAccess('PM_FALLBACK') ? { create: { label: 'Add organization', roles: ['admin', 'sales'] } } : {}),
        activeIcon: 'I-S-Organizations-Fill',
        iconTone: 'subtle',
        activeIconTone: 'brand',
        iconHover: false,
        adminFallback: true,
        fallbackRoles: ['admin', 'sales', 'orgAdmin'],
        permissions: ['users_list'],
      },
      requiresAuth: true,
      title: 'Users',
      requiredPermissions: ['users_list']
    }
  },
  {
    path: '/users/role/:id?',
    name: 'user-role',
    component: () => import('../pages/users/sub-pages/RoleForm.vue'),
    meta: {
      requiresAuth: true,
      title: 'Role Management',
      requiredPermissions: ['permission_manager_get']
    },
  },
  {
    path: '/users/organizations/:id',
    name: 'organization-details',
    component: () => import('../pages/users/organizations/details/index.vue'),
    meta: {
      requiresAuth: true,
      title: 'Organization',
      requiredPermissions: ['users_list'],
    },
  },
  {
    path: '/users/:id',
    name: 'view-user-profile',
    component: () => import('../pages/users/sub-pages/UserProfile.vue'),
    meta: { requiresAuth: true, title: 'User Profile' },
  },
  {
    path: '/alerts',
    name: 'alerts',
    component: () => import(/* webpackChunkName: "alerts" */ '../pages/alerts'),
    meta: {
      menu: {
        section: 'kyt',
        order: 40,
        title: 'Alerts',
        icon: 'Alerts',
        activeIcon: 'Alerts-fill',
        feature: 'ALERTS',
        permissions: ['alert_list'],
        counter: 'unreadNotifications',
      },
      requiresAuth: true,
      title: 'Alerts',
      requiredPermissions: ['alert_list']
    },
  },
  {
    path: '/alerting',
    name: 'alerting',
    component: () => import(/* webpackChunkName: "Alerting" */ '../pages/alerting'),
    meta: {
      menu: {
        section: 'settings',
        order: 20,
        title: 'Alert Settings',
        icon: 'Alert-settings',
        create: { label: 'Create alert', permissions: ['alert_modify'] },
        activeIcon: 'Alert-settings-fill',
        feature: 'ALERTS',
        permissions: ['alert_get', 'alert_list', 'alert_modify'],
      },
      requiresAuth: true,
      title: 'Alert Settings',
      requiredPermissions: ['alert_list']
    },
  },
  {
    path: '/labeling',
    name: 'labeling',
    component: () => import(/* webpackChunkName: "tagging" */ '../pages/tagging'),
    meta: {
      menu: {
        section: 'admin',
        order: 20,
        title: 'Labels',
        icon: 'Labels',
        activeIcon: 'Labels-fill',
        adminFallback: true,
        permissions: ['labels_get'],
        query: { tab: 'labeling' },
        notifyKey: 'labeling',
      },
      requiresAuth: true,
      title: 'Labels',
      requiredPermissions: ['labels_get']
    },
  },
  {
    path: '/statistics',
    name: 'statistics',
    component: () => import(/* webpackChunkName: "statistics" */ '../pages/statistics'),
    meta: {
      menu: {
        section: 'admin',
        order: 30,
        title: 'Statistics',
        icon: 'Statistics',
        activeIcon: 'Statistics-fill',
        adminFallback: true,
        permissions: ['statistics_list'],
        query: { tab: 'clusters' },
      },
      requiresAuth: true,
      title: 'Statistics',
      requiredPermissions: ['statistics_list']
    },
  },
  {
    path: '/scoring',
    name: 'scoring',
    component: () => import(/* webpackChunkName: "scoring" */ '../pages/scoring'),
    meta: {
      menu: {
        section: 'admin',
        order: 40,
        title: 'Scores',
        icon: 'Scores',
        activeIcon: 'Scores-fill',
        adminFallback: true,
        permissions: ['scores_list'],
        notifyKey: 'scoring',
      },
      requiresAuth: true,
      title: 'Scores',
      requiredPermissions: ['scores_list']
    },
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import(/* webpackChunkName: "vision" */ '../pages/analytics'),
    meta: {
      menu: {
        section: 'investigations',
        order: 10,
        title: 'Tracing',
        icon: 'Tracing',
        activeIcon: 'Tracing-fill',
        feature: 'VISION',
        permissions: ['tracing_get'],
      },
      requiresAuth: true,
      title: 'Tracing',
      requiredPermissions: ['tracing_get']
    },
  },
  {
    path: '/cases',
    name: 'cases',
    component: featureAccess('CASES_NEW')
      ? () => import(/* webpackChunkName: "cases" */ '../pages/cases-new')
      : () => import(/* webpackChunkName: "cases" */ '../pages/cases'),
    meta: {
      menu: {
        section: 'investigations',
        order: 30,
        title: 'Case Files',
        icon: 'Cases',
        activeIcon: 'Cases-fill',
        feature: 'MY_CASES',
        permissions: ['trace_cases_modify'],
      },
      requiresAuth: true,
      title: 'Case Files',
      requiredPermissions: ['trace_cases_list']
    },
  },
  {
    path: '/report',
    name: 'report',
    component: () => import(/* webpackChunkName: "report" */ '../pages/report'),
    meta: {
      menu: {
        section: 'kyt',
        order: 10,
        title: 'Risk Report',
        icon: 'Risk-report',
        activeIcon: 'Risk-report-fill',
        feature: 'REPORT',
        permissions: ['report_get'],
      },
      requiresAuth: true,
      title: 'Risk Report',
      requiredPermissions: ['report_get']
    },
  },
  ...(featureAccess('SCREENING') ? [
    {
      path: '/screening',
      name: 'screening',
      component: () => import(/* webpackChunkName: "screening" */ '../pages/screening'),
      meta: {
        // Sits between Risk Report (10) and KYT Lab (20), where it was before the
        // menu moved into the router. `feature` repeats the gate the route is
        // already wrapped in - the same belt-and-braces USER_GUIDE uses - so the
        // link stays hidden even if the route is ever registered unconditionally.
        menu: {
          section: 'kyt',
          order: 15,
          title: 'Screening Report',
          icon: 'Screening-report',
          activeIcon: 'Screening-report-fill',
          feature: 'SCREENING',
        },
        requiresAuth: true,
        title: 'Screening Report',
      },
    },
  ] : []),
  {
    path: '/counterparty',
    name: 'counterparty',
    component: () => import(/* webpackChunkName: "counterparty" */ '../pages/counterparty'),
    meta: {
      menu: {
        section: 'kyb',
        order: 20,
        title: 'Entity Report',
        icon: 'Entity-exposure',
        create: { label: 'Create entity report', permissions: ['entity_exposure_modify'] },
        activeIcon: 'Entity-exposure-fill',
        feature: 'COUNTERPARTY',
        permissions: ['entity_exposure_list'],
      },
      requiresAuth: true,
      title: 'Entity Report'
    },
    children: [
      {
        path: '/counterparty/report/:reportId',
        name: 'view-counterparty-report',
        component: () => import(/* webpackChunkName: "view-counterparty-report" */ '../pages/counterparty/components/ViewReport'),
        meta: {
          requiresAuth: true,
          title: 'Entity Report',
          requiredPermissions: ['entity_exposure_get']
        },
      },
    ]
  },
  {
    path: '/monitoring',
    name: 'monitoring',
    component: () => import(/* webpackChunkName: "monitoring" */ '../pages/monitoring'),
    meta: {
      menu: {
        section: 'kyt',
        order: 30,
        title: 'Monitoring List',
        icon: 'Monitoring',
        create: { label: 'Add address to monitoring', permissions: ['monitoring_modify'] },
        activeIcon: 'Monitoring-fill',
        feature: 'MONITORING',
        permissions: ['monitoring_list'],
      },
      requiresAuth: true,
      title: 'Monitoring List',
      requiredPermissions: ['monitoring_list']
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import(/* webpackChunkName: "profile" */ '../pages/profile'),
    meta: {
      menu: {
        section: 'settings',
        order: 10,
        title: 'My Account',
        icon: 'My-account',
        activeIcon: 'My-account-fill',
      },
      requiresAuth: true,
      title: 'My Account'
    },
  },
  {
    path: '/scoring-profiles',
    name: 'scoring-profiles',
    component: () => import(/* webpackChunkName: "scoring-profiles" */ '../pages/scoring-profiles'),
    meta: {
      menu: {
        section: 'settings',
        order: 30,
        title: 'Tags & Types',
        icon: 'My-scorings',
        create: { label: 'Create profile' },
        activeIcon: 'My-scorings-fill',
        feature: 'SCORING_PROFILES',
        notRoles: ['customer'],
      },
      requiresAuth: true,
      title: 'Tags & Types'
    },
  },
  {
    path: '/entity-explorer',
    component: () => import(/* webpackChunkName: "entity" */ '../pages/entity'),
    children: [
      {
        path: '',
        name: 'entity-list',
        component: () => import(/* webpackChunkName: "entity-list" */ '../pages/entity/entity-list'),
        meta: {
          menu: {
            key: 'entity',
            section: 'kyb',
            order: 10,
            title: 'Entity Database',
            icon: 'Entity-database',
            create: { label: 'Create entity', route: 'create-entity-management', permissions: ['entity_list', 'entity_get'] },
            activeIcon: 'Entity-database-fill',
            feature: 'ENTITY',
            permissions: ['entity_list'],
          },
          title: 'Entity Database',
          requiredPermissions: ['entity_list']
        },
      },
      {
        path: 'view/:entityId',
        name: 'view-entity-explorer',
        component: () => import(/* webpackChunkName: "entity-view" */ '../pages/entity/entity-view'),
        meta: {
          title: 'Entity Database',
          requiredPermissions: ['entity_get']
        },
      },
    ],
    name: 'entity-explorer',
    meta: {
      requiresAuth: true,
      title: 'Entity Database',
      requiredPermissions: ['entity_list']
    },
  },
  {
    path: '/entity-management',
    component: () => import(/* webpackChunkName: "entity-management" */ '../pages/entity'),
    meta: {
      menu: {
        key: 'entity-management',
        section: 'admin',
        order: 50,
        title: 'Entity Manager',
        icon: 'Entities',
        create: { label: 'Create entity', route: 'create-entity-management', permissions: ['entity_list', 'entity_get'] },
        activeIcon: 'Entities-fill',
        adminFallback: true,
        permissions: ['entity_modify', 'entity_list'],
        permissionsFeature: 'ENTITY',
      },
      requiresAuth: true,
      title: 'Entity Manager',
      requiredPermissions: ['entity_list', 'entity_get']
    },
    children: [
      {
        path: '',
        name: 'entity-management-list',
        component: () => import(/* webpackChunkName: "entity-list" */ '../pages/entity/entity-list'),
        meta: {
          title: 'Entity Manager',
          requiredPermissions: ['entity_list', 'entity_get']
        },
      },
      {
        path: 'view/:entityId',
        name: 'view-entity-management',
        component: () => import(/* webpackChunkName: "view-entity-management" */ '../pages/entity/entity-view'),
        meta: {
          requiresAuth: true,
          title: 'Entity Manager',
          requiredPermissions: ['entity_list', 'entity_get']
        },
      },
      {
        path: 'create',
        name: 'create-entity-management',
        component: () => import(/* webpackChunkName: "create-entity-management" */ '../pages/entity/entity-management'),
        meta: {
          requiresAuth: true,
          title: 'Create Entity',
          requiredPermissions: ['entity_list', 'entity_get']
        },
      },
      {
        path: 'edit/:entityId',
        name: 'edit-entity-management',
        component: () => import(/* webpackChunkName: "edit-entity-management" */ '../pages/entity/entity-management'),
        meta: {
          requiresAuth: true,
          title: 'Edit Entity',
          requiredPermissions: ['entity_list', 'entity_get']
        },
      },
    ]
  },
  {
    path: '/integrations',
    component: () => import(/* webpackChunkName: "integrations" */ '../pages/integrations/layout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'integrations-list',
        component: () => import(/* webpackChunkName: "integrations" */ '../pages/integrations/list/index.vue'),
        meta: {
          menu: {
            key: 'integrations',
            section: 'footer',
            order: 10,
            title: 'Integrations',
            icon: 'I-S-Integrations',
            activeIcon: 'I-S-Integrations-Fill',
            iconTone: 'subtle',
            activeIconTone: 'brand',
            iconHover: false,
          },
          title: 'Integrations',
        },
      },
      {
        path: ':id/connect',
        name: 'integration-connect',
        component: () => import(/* webpackChunkName: "integrations" */ '../pages/integrations/connect/index.vue'),
      },
      {
        path: ':id',
        name: 'integration-details',
        component: () => import(/* webpackChunkName: "integrations" */ '../pages/integrations/details/index.vue'),
      },
    ],
  },
  {
    path: '/403',
    name: '403',
    component: () => import(/* webpackChunkName: "403" */ '../pages/403'),
  },
  ...(featureAccess('COMPLIANCE_MEMORY') ? [
    {
      path: '/api-requests',
      component: () => import(/* webpackChunkName: "api-requests" */ '../pages/api-requests-new'),
      meta: {
        requiresAuth: true,
        title: 'History',
        requiredPermissions: ['report_history_list']
      },
      children: [
        {
          path: '',
          name: 'api-requests',
          meta: {
            menu: {
              section: 'kyt',
              order: 50,
              title: 'History',
              icon: 'I-History',
              activeIcon: 'I-History-Fill',
              feature: 'SCORE_REQUESTS',
              notRoles: ['customer'],
              permissions: ['report_history_list'],
            },
            requiresAuth: true,
            title: 'History',
            requiredPermissions: ['report_history_list']
          },
          component: () => import('../pages/api-requests-new/api-requests')
        },
        {
          path: 'action-memory/:address/:coin',
          name: 'action-memory-case',
          component: () => import(/* webpackChunkName: "action-memory-case" */ '../pages/api-requests-new/action-memory'),
          props: true,
          meta: {
            requiresAuth: true,
            title: 'Address Timeline',
            requiredPermissions: ['report_history_list']
          }
        }
      ]
    },
  ] : [
    {
      path: '/api-requests',
      name: 'api-requests',
      component: () => import(/* webpackChunkName: "api-requests" */ '../pages/api-requests'),
      meta: {
        menu: {
          section: 'kyt',
          order: 50,
          title: 'History',
          icon: 'I-History',
          activeIcon: 'I-History-Fill',
          feature: 'SCORE_REQUESTS',
          notRoles: ['customer'],
          permissions: ['report_history_list'],
        },
        requiresAuth: true,
        title: 'History',
        requiredPermissions: ['report_history_list']
      },
    },
  ]),
  ...(featureAccess('USER_GUIDE') ? [
    {
      path: '/user-guide',
      name: 'user-guide',
      component: () => import(/* webpackChunkName: "user-guide" */ '../pages/user-guide'),
      meta: {
        menu: {
          section: 'footer',
          order: 30,
          title: 'User Guide',
          icon: 'User-guide-book',
          activeIcon: 'User-guide-book-fill',
          feature: 'USER_GUIDE',
        },
        requiresAuth: true,
        title: 'User Guide',
        requiredPermissions: ['user_guide_list']
      },
    },
    {
      path: '/guide-manager',
      name: 'guide-manager',
      component: () => import(/* webpackChunkName: "guide-manager" */ '../pages/guide-manager'),
      meta: {
        menu: {
          key: 'guide-manage',
          section: 'admin',
          order: 60,
          title: 'User Guide Manager',
          icon: 'User-guide',
          create: { label: 'Create user guide' },
          activeIcon: 'User-guide-fill',
          adminFallback: true,
          permissions: ['user_guide_list', 'user_guide_modify'],
          permissionsFeature: 'USER_GUIDE',
        },
        requiresAuth: true,
        title: 'User Guide Manager',
        requiredPermissions: ['user_guide_list', 'user_guide_modify']
      },
    },
  ] : []),
  ...(featureAccess('AUTO_TRACE') ? [
    {
      path: '/autotrace',
      component: () => import(/* webpackChunkName: "autotrace" */ '../pages/autotrace'),
      children: [
        {
          path: '',
          name: 'trace-list',
          component: () => import(/* webpackChunkName: "trace-list" */ '../pages/autotrace/trace-list'),
          meta: {
            menu: {
              key: 'autotrace',
              section: 'investigations',
              order: 20,
              title: 'Auto Tracing',
              icon: 'Auto-trace',
              create: { label: 'New auto-trace case', permissions: ['auto_trace_modify'] },
              activeIcon: 'Auto-trace-fill',
              feature: 'AUTO_TRACE',
              permissions: ['auto_trace_modify'],
            },
            title: 'Auto Tracing',
            requiredPermissions: ['auto_trace_list']
          },
        },
        {
          path: 'case/:caseId',
          name: 'trace-case',
          component: () => import(/* webpackChunkName: "trace-case" */ '../pages/autotrace/trace-case'),
          meta: {
            title: 'Autotrace Incident',
            requiredPermissions: ['auto_trace_get']
          },
        },
      ],
      meta: {
        requiresAuth: true,
        title: 'Auto Tracing',
        requiredPermissions: ['auto_trace_list']
      },
    },
  ] : []),
  ...(featureAccess('DEFI_EXPOSURE') ? [
    {
      path: '/defi-exposure',
      component: () => import(/* webpackChunkName: "defi-exposure" */ '../pages/defi-exposure'),
      children: [
        {
          path: '',
          name: 'defi-exposure-list',
          component: () => import(/* webpackChunkName: "defi-exposure-list" */ '../pages/defi-exposure/list'),
          meta: {
            menu: {
              key: 'defi-exposure',
              section: 'kyb',
              order: 30,
              title: 'DeFi Report',
              icon: 'defi-exposure',
              create: { label: 'Create DeFi report', permissions: ['defi_modify'] },
              activeIcon: 'defi-exposure-fill',
              feature: 'DEFI_EXPOSURE',
              permissions: ['defi_list', 'defi_modify'],
            },
            title: 'DeFi Report',
            requiredPermissions: ['defi_list']
          },
        },
        {
          path: 'report/:reportId',
          name: 'defi-exposure-report',
          component: () => import(/* webpackChunkName: "defi-exposure-report" */ '../pages/defi-exposure/report'),
          meta: {
            title: 'DeFi Report',
            requiredPermissions: ['defi_get']
          },
        },
      ],
      meta: {
        requiresAuth: true,
        title: 'DeFi Report',
        requiredPermissions: ['defi_list']
      },
    },
  ] : []),
  ...(featureAccess('KYT_LAB') ? [
    {
      path: '/kyt-lab',
      component: () => import(/* webpackChunkName: "kyt-lab" */ '../pages/kyt-lab'),
      children: [
        {
          path: '',
          name: 'kyt-lab-list',
          component: () => import(/* webpackChunkName: "kyt-lab-list" */ '../pages/kyt-lab/list'),
          meta: {
            menu: {
              key: 'kyt-lab',
              section: 'kyt',
              order: 20,
              title: 'KYT Lab',
              icon: 'kyt-lab',
              activeIcon: 'kyt-lab-fill',
              feature: 'KYT_LAB',
              permissions: ['kyt_lab_list'],
            },
            title: 'KYT Lab',
            requiredPermissions: ['kyt_lab_list']
          },
        },
        {
          path: 'overview/:taskId',
          name: 'kyt-lab-overview',
          component: () => import(/* webpackChunkName: "kyt-lab-overview" */ '../pages/kyt-lab/overview'),
          meta: {
            title: 'KYT Overview',
            requiredPermissions: ['kyt_lab_get']
          },
        },
      ],
      meta: {
        requiresAuth: true,
        title: 'KYT Lab',
      },
    },
  ] : []),
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard'),
    meta: {
      menu: {
        section: 'dashboard',
        order: 10,
        title: 'Dashboard',
        icon: 'I-S-Dashboard',
        activeIcon: 'I-S-Dashboard-Fill',
        feature: 'DASHBOARD',
      },
      requiresAuth: true,
      title: 'Dashboard',
    },
  },
  {
    path: '/404',
    name: '404',
    component: () => import(/* webpackChunkName: "404" */ '../pages/404'),
    meta: {
      title: 'Page not found',
    },
  },
  // Must stay last: it swallows everything the routes above did not match.
  {
    path: '*',
    redirect: { name: '404' },
  },
]

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes,
})

const adminRoutesList = ['users', 'labeling', 'statistics', 'scoring', 'entity-management', 'guide-manager']
const essentialRoutesList = ['report', 'screening', 'api-requests', 'analytics']

router.beforeEach(async (to, from, next) => {
  if (to.query.caseId && to.query.public) {
    next()
    return
  }

  if (to.name === 'login' || to.name === 'enter-code') {
    const token = await store.dispatch('user/initToken')
    if (token) {
      next({ name: defaultRouteName })
      return
    }
  }

  if (to.name === 'register') {
    await store.dispatch('user/logout')
    next()
    return
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    const token = await store.dispatch('user/initToken')
    if (!token) {
      next({ name: 'login' })
      return
    }

    localStorage.setItem('to.fullPath', to.fullPath)

    const isRejected = await store.dispatch('user/getMe')

    if (isRejected) return

    const isAdmin = store.getters["user/isAdmin"]
    const isOrgAdmin = store.getters["user/isOrgAdmin"]
    const organizationId = store.state.user?.userData?.organizationId

    if (isAdmin) {
      sidebarTrackingMixin.methods.checkUntackedLabels()
    }

    // if (isOrgAdmin) {
    if (isOrgAdmin && !isAdmin) {
      if (!organizationId) {
        next({ name: '403' })
        return
      }

      if (to.name === 'users') {
        next({
          name: 'organization-details',
          params: { id: organizationId },
          query: { tab: 'team' },
        })
        return
      }

      if (
        to.name === 'organization-details' &&
        String(to.params.id) !== String(organizationId)
      ) {
        next({ name: '403' })
        return
      }
    }
  }

  const requiredPermissions = to.meta.requiredPermissions;

  if (requiredPermissions) {
    const userPermissions = await store.getters["user/permissions"];
    const hasAccess = checkPermissions(userPermissions, requiredPermissions);

    if (!hasAccess) {
      if (to.name.includes('analytics')) {
        next({ name: '403' })
        return
      }

      return next({ name: '403' });
    }
  }

  // const hasEntityAccess = await store.getters["user/hasEntityAccess"]
  //
  // if (to.name.includes('entity') && !hasEntityAccess) {
  //   next({ name: 'analytics' })
  //   return
  // }

  if (adminRoutesList.find(route => route === to.name)) {
    const userPermissions = await store.getters["user/permissions"];
    const hasAccess = checkPermissions(userPermissions, requiredPermissions);
    const isAdmin = await store.getters["user/isAdmin"]
    const isSales = await store.getters["user/isSales"]
    const isOrgAdmin = await store.getters["user/isOrgAdmin"]

    if (to.name === 'users' && ((isSales || isOrgAdmin)) || hasAccess) {
      next()
      return
    }

    if (!isAdmin) {
      next({ name: '403' })
      return
    }
  }

  next()
})

router.afterEach((to, /* from */) => {
  if (store.state.analytics.coinData.essential && !essentialRoutesList.includes(to.name)) {
    const coin = store.state.analytics.currencyList[0]

    if (coin) {
      store.commit('analytics/SET_COIN_DATA', coin)
      store.commit('analytics/SET_COIN_TYPE', coin.key)
    }
  }

  Vue.nextTick(() => {
    const searchValue = to.query.address ?? to.query.tx
    const isOrgAdmin = store.getters['user/isOrgAdmin']
    const pageTitle =
      to.name === 'users'
        ? (isOrgAdmin ? 'Organization' : 'Organizations')
        : to.meta.title

    document.title = searchValue
      ? `${pageTitle} - ${searchValue}`
      : pageTitle || 'gl-front-vision'
  })
})

router.onError(error => {
  console.error(error);
  if (/ChunkLoadError:.*failed./i.test(error.message)) {
    window.location.reload();
  }
  else if (/Loading.*chunk.*failed./i.test(error.message)) {
    window.location.reload();
  }
});

export default router
